import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Slider } from './ui/slider';
import { Input } from './ui/input';
import { formatCurrency, formatNumber } from '../utils/calculations';
import CarbonEmissionsChart from './charts/CarbonEmissionsChart';
import { Globe, FileText, Presentation, Mail, BookOpen } from 'lucide-react';

const DataTable = ({ metrics, turbineCount, inputValue, handleSliderChange, handleInputChange, openSlideshow }) => {
  const macroBenefits = [
    { label: 'Annual generation (MWh)', value: metrics.annualGenerationMWh, formatter: (v) => formatNumber(v, 0) },
    { label: 'Lifetime generation (GWh)', value: metrics.lifetimeGenerationGWh, formatter: (v) => formatNumber(v, 0) },
    { label: 'Annual CO\u2082 avoided (tCO\u2082)', value: metrics.annualCO2Tonnes, formatter: (v) => formatNumber(v, 0) },
    { label: 'Lifetime CO\u2082 avoided (ktCO\u2082)', value: metrics.lifetimeCO2Kilotonnes, formatter: (v) => formatNumber(v, 1) },
    { label: 'Annual community fund benefit (\u00a3)', value: metrics.annualCommunityBenefit, formatter: (v) => formatCurrency(v, 1) },
    { label: 'Lifetime community fund benefit (\u00a3, 25yr indexed)', value: metrics.lifetimeCommunityBenefit, formatter: (v) => formatCurrency(v, 1) },
    { label: 'Jobs created (FTE)', value: metrics.jobsFTE, formatter: (v) => formatNumber(v, 1) },
    { label: 'Leveraged capital (Built, \u00a3)', value: metrics.leveragedCapital, formatter: (v) => formatCurrency(v, 1) },
    { label: 'Business rates to LAs (\u00a3/year)', value: metrics.annualBusinessRates, formatter: (v) => formatCurrency(v, 0) },
    { label: 'Business rates to LAs (25yr total, \u00a3)', value: metrics.lifetimeBusinessRates, formatter: (v) => formatCurrency(v, 1) },
    { label: 'Land rent to hosts (\u00a3/year)', value: metrics.annualLandRent, formatter: (v) => formatCurrency(v, 0) },
    { label: 'Land rent to hosts (25yr total, \u00a3)', value: metrics.lifetimeLandRent, formatter: (v) => formatCurrency(v, 1) },
    { label: 'Homes powered (annually)', value: metrics.homesPowered, formatter: (v) => formatNumber(v, 0) }
  ];

  // Use the full phasing table from calculations
  const displayYears = metrics.displayYears;
  const phasingData = metrics.phasingTable;

  return (
    <>
      {/* First Row: Macro Benefits and Carbon Emissions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Left: Macro Benefits Card */}
        <Card className="shadow-lg border-emerald-200">
          <CardHeader className="bg-white border-b border-emerald-100">
            <CardTitle className="text-lg" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
              Macro Benefits
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
              Auto-calculated from turbine count
            </p>
          </CardHeader>
          <CardContent className="pt-6 bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-emerald-50">
                  <TableHead className="font-semibold" style={{ color: '#2C3E50', paddingRight: '8px' }}>Metric</TableHead>
                  <TableHead className="text-right font-semibold" style={{ color: '#2C3E50', paddingLeft: '8px' }}>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {macroBenefits.map((item, index) => (
                  <TableRow 
                    key={index} 
                    className={`transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-emerald-50`}
                  >
                    <TableCell className="font-medium py-2 pr-2" style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.875rem' }}>
                      {item.label}
                    </TableCell>
                    <TableCell className="text-right font-semibold py-2 pl-2" style={{ color: '#2C3E50', fontSize: '0.875rem' }}>
                      {item.formatter(item.value)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Right: Carbon Emissions Card */}
        <Card className="shadow-lg border-emerald-200">
          <CardHeader className="bg-white border-b border-emerald-100">
            <CardTitle className="text-lg" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
              Carbon Emissions Avoided (Lifetime Impact)
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
              Demonstrates cumulative carbon savings from the community wind portfolio across its 30-year operational life
            </p>
          </CardHeader>
          <CardContent className="pt-4 bg-white">
            <CarbonEmissionsChart metrics={metrics} />
            
            {/* Compact Turbine Selector */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Quick Adjust Turbine Numbers
                </label>
                <Input
                  type="number"
                  min={1}
                  max={125}
                  value={inputValue}
                  onChange={handleInputChange}
                  className="w-16 h-8 text-center text-sm"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                />
              </div>
              <Slider
                value={[turbineCount]}
                onValueChange={handleSliderChange}
                min={1}
                max={125}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100</span>
                <span>125</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row: Phasing Table (Full Width) */}
      <Card className="shadow-lg border-emerald-200">
        <CardHeader className="bg-white border-b border-emerald-100">
          <CardTitle className="text-lg" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
            Development & Deployment Timeline
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
            Annual breakdown of turbine deployment, development investment, and DEVEX recycling over {displayYears} years. In reality, recycling the DEVEX may be a little messier depending on financing route. However, we are confident that all of the DEVEX can be recycled albeit it might take a year or two longer than displayed in this simple model.
          </p>
        </CardHeader>
        <CardContent className="pt-6 bg-white">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-emerald-50">
                  <TableHead className="font-semibold" style={{ color: '#2C3E50' }}>Year</TableHead>
                  <TableHead className="text-right font-semibold" style={{ color: '#2C3E50' }}>Started</TableHead>
                  <TableHead className="text-right font-semibold" style={{ color: '#2C3E50' }}>Installed</TableHead>
                  <TableHead className="text-right font-semibold" style={{ color: '#2C3E50' }}>In Development EOY</TableHead>
                  <TableHead className="text-right font-semibold" style={{ color: '#2C3E50' }}>Annual Development Budget (£)</TableHead>
                  <TableHead className="text-right font-semibold" style={{ color: '#2C3E50' }}>Recycled DEVEX (£)</TableHead>
                  <TableHead className="text-right font-semibold" style={{ color: '#2C3E50' }}>Net Funding (£)</TableHead>
                  <TableHead className="text-right font-semibold" style={{ color: '#2C3E50' }}>Cumulative Net Funding (£)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {phasingData.map((row, index) => (
                  <TableRow key={index} className="hover:bg-emerald-50 transition-colors">
                    <TableCell className="font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
                      Year {row.year}
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#2C3E50' }}>
                      {formatNumber(row.started, 0)}
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#2C3E50' }}>
                      {formatNumber(row.installed, 0)}
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#2C3E50' }}>
                      {formatNumber(row.inDevelopmentEOY, 0)}
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#2C3E50' }}>
                      {formatCurrency(row.annualDevelopment, 2)}
                    </TableCell>
                    <TableCell className="text-right" style={{ color: '#2C3E50' }}>
                      {formatCurrency(row.recycledDevex, 2)}
                    </TableCell>
                    <TableCell className="text-right font-semibold" style={{ color: row.netFunding < 0 ? '#48A46A' : '#2C3E50' }}>
                      {formatCurrency(row.netFunding, 2)}
                    </TableCell>
                    <TableCell className="text-right font-semibold" style={{ color: '#2C3E50' }}>
                      {formatCurrency(row.cumulativeNetFunding, 2)}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow className="bg-emerald-100 font-bold">
                  <TableCell colSpan={4} className="font-bold" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Totals
                  </TableCell>
                  <TableCell className="text-right font-bold" style={{ color: '#2C3E50' }}>
                    {formatCurrency(phasingData.reduce((sum, row) => sum + row.annualDevelopment, 0), 2)}
                  </TableCell>
                  <TableCell className="text-right font-bold" style={{ color: '#2C3E50' }}>
                    {formatCurrency(phasingData.reduce((sum, row) => sum + row.recycledDevex, 0), 2)}
                  </TableCell>
                  <TableCell className="text-right font-bold" style={{ color: '#2C3E50' }}>
                    {formatCurrency(phasingData.reduce((sum, row) => sum + row.netFunding, 0), 2)}
                  </TableCell>
                  <TableCell className="text-right font-bold" style={{ color: '#2C3E50' }}>
                    {formatCurrency(phasingData[phasingData.length - 1]?.cumulativeNetFunding || 0, 2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Third Row: Combined Strategic Alignment Block */}
      <Card className="shadow-lg border-emerald-200 mt-8">
        <CardContent className="pt-6 pb-6 bg-white">
          <div className="grid grid-cols-12 gap-8">
            {/* Left Column: Heading and Text (narrower - 5 columns) */}
            <div className="col-span-5">
              <h3 className="text-lg font-bold mb-4" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                GBE Strategic Alignment — Programme Benefits
              </h3>
              
              <p className="text-xl text-gray-700" style={{ fontFamily: 'Lato, sans-serif', lineHeight: '1.6' }}>
                This proposal is not an aspirational study, but a deliverable programme built on CPS's direct experience developing 100% community-owned, megawatt-scale turbines. It offers GBE a clear route to action and brings all of GBE's stated strategic objectives together in one coherent programme.
              </p>
            </div>
            
            {/* Right Column: Tick Matrix - 2 columns, left-aligned, full height (7 columns) */}
            <div className="col-span-7 pl-20">
              <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                {/* Column 1 - First 5 items */}
                <div className="text-left">
                  <span className="text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                    ✔️ <span className="font-bold">Delivers Clean Power</span>
                  </span>
                </div>
                
                <div className="text-left">
                  <span className="text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                    ✔️ <span className="font-bold">Backs UK Supply Chains</span>
                  </span>
                </div>
                
                <div className="text-left">
                  <span className="text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                    ✔️ <span className="font-bold">Derisks Development</span>
                  </span>
                </div>
                
                <div className="text-left">
                  <span className="text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                    ✔️ <span className="font-bold">Expands Community Energy</span>
                  </span>
                </div>
                
                <div className="text-left">
                  <span className="text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                    ✔️ <span className="font-bold">Unlocks Investment</span>
                  </span>
                </div>
                
                <div className="text-left">
                  <span className="text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                    ✔️ <span className="font-bold">Supports Local Power Plan</span>
                  </span>
                </div>
                
                <div className="text-left">
                  <span className="text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                    ✔️ <span className="font-bold">Builds Community Ownership</span>
                  </span>
                </div>
                
                <div className="text-left">
                  <span className="text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                    ✔️ <span className="font-bold">Reinvests Public Returns</span>
                  </span>
                </div>
                
                <div className="text-left">
                  <span className="text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                    ✔️ <span className="font-bold">Creates Good Jobs</span>
                  </span>
                </div>
                
                <div className="text-left">
                  <span className="text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                    ✔️ <span className="font-bold">Public Ownership Options</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Resources Toolbar */}
      <Card className="shadow-lg border-emerald-200 mt-8">
        <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-50 border-b border-emerald-100">
          <div className="flex items-center justify-between">
            {/* Left Section - Text */}
            <div>
              <CardTitle className="text-lg mb-1" style={{ color: '#2C3E50', fontFamily: 'Montserrat, sans-serif' }}>
                Additional Resources & Contact Details
              </CardTitle>
              <p className="text-sm text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
                Further information and supporting materials relating to the CPS community wind programme.
              </p>
            </div>
            
            {/* Right Section - Icons */}
            <div className="flex items-center gap-8">
              {/* Executive Summary */}
              <a 
                href="https://customer-assets.emergentagent.com/job_turbine-metrics/artifacts/t65gp2r1_GBE_Community_Wind.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 hover:opacity-70 transition-opacity"
              >
                <BookOpen className="w-8 h-8 text-emerald-600" />
                <span className="text-xs font-medium text-gray-700" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Executive Summary
                </span>
              </a>
              
              {/* Programme Brochure */}
              <a 
                href="https://www.yumpu.com/en/document/read/69816816/scaling-up-community-owned-onshore-wind-power" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 hover:opacity-70 transition-opacity"
              >
                <FileText className="w-8 h-8 text-emerald-600" />
                <span className="text-xs font-medium text-gray-700" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Programme Brochure
                </span>
              </a>
              
              {/* Briefing Slides */}
              <button 
                onClick={openSlideshow}
                className="flex flex-col items-center gap-2 hover:opacity-70 transition-opacity cursor-pointer bg-transparent border-none"
              >
                <Presentation className="w-8 h-8 text-emerald-600" />
                <span className="text-xs font-medium text-gray-700" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Briefing Slides
                </span>
              </button>
              
              {/* CPS Website */}
              <a 
                href="https://www.communitypowersolutions.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 hover:opacity-70 transition-opacity"
              >
                <Globe className="w-8 h-8 text-emerald-600" />
                <span className="text-xs font-medium text-gray-700" style={{ fontFamily: 'Lato, sans-serif' }}>
                  CPS Website
                </span>
              </a>
              
              {/* Contact CPS */}
              <a 
                href="mailto:iain.beveridge@communitypowersolutions.co.uk" 
                className="flex flex-col items-center gap-2 hover:opacity-70 transition-opacity"
              >
                <Mail className="w-8 h-8 text-emerald-600" />
                <span className="text-xs font-medium text-gray-700" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Contact CPS
                </span>
              </a>
            </div>
          </div>
        </CardHeader>
      </Card>
    </>
  );
};

export default DataTable;
